import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import SectionHeading from './ui/SectionHeading';
import StarBorder from './ui/StarBorder';
import SkillsMarquee from './ui/SkillsMarquee';
import CountUp from './ui/CountUp';

const skillRows: string[][] = [
  [
    'Apigee X & Hybrid', 'API Proxy Development & Migration', 'Google Cloud Platform',
    'Cloud Run', 'Cloud SQL', 'AWS Compute', 'IAM & Access Control',
    'DNS & SSL Configuration', 'Hybrid Connectivity Troubleshooting',
    'Logging & Monitoring Integration', 'Networking Architectures',
  ],
  [
    'Python', 'C++', 'Java', 'JavaScript', 'HTML', 'CSS', 'C# (Socket Programming)',
    'Android Development', 'Full-Stack Web Development', 'Database Design',
    'Python GUI Development', 'Selenium', 'Excel', 'Computational Problem Solving',
  ],
];

const stats = [
  { value: 15, suffix: '+', label: 'Projects Completed' },
  { value: 2, suffix: '+', label: 'Years Experience' },
  { value: 50, suffix: '+', label: 'API Proxies Migrated' },
];

const About: React.FC = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        delayChildren: 0.3,
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: { y: 0, opacity: 1 }
  };

  return (
    <section id="about" ref={ref} className="section-padding px-6 bg-gradient-to-b from-transparent to-dark-100/20">
      <div className="max-w-6xl mx-auto">
        <SectionHeading eyebrow="Who I Am" title="About Me" />

        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
        >
          <div className="grid lg:grid-cols-2 gap-12 items-start mb-12">
            <motion.div variants={itemVariants} className="space-y-6">
              <p className="text-lg text-gray-300 leading-relaxed">
                I'm a Jr. Integration Specialist at Abacus Consulting, focused on Apigee X/Hybrid
                API proxy development and migration, Google Cloud infrastructure, and enterprise
                integration engineering. I graduated from Sabanci University in Istanbul with a strong
                foundation in software engineering and cloud computing.
              </p>

              <p className="text-lg text-gray-300 leading-relaxed">
                My work involves validating and migrating API proxies across organisations,
                troubleshooting hybrid cloud connectivity, and driving API platform R&D across
                Apigee X/Hybrid and Google Cloud environments.
              </p>

              <p className="text-lg text-gray-300 leading-relaxed">
                Beyond my primary role, I've built ML-powered cybersecurity tools, a solar
                quotation platform, and an autonomous farming system — always driven by creating
                practical, scalable solutions to real-world problems.
              </p>
            </motion.div>

            <motion.div variants={itemVariants} className="space-y-6">
              {/* Stats with count-up numbers */}
              <div className="grid grid-cols-3 gap-4">
                {stats.map((stat, index) => (
                  <div key={index} className="glass-effect rounded-xl p-5 text-center">
                    <div className="text-3xl font-bold gradient-text mb-2">
                      <CountUp to={stat.value} suffix={stat.suffix} />
                    </div>
                    <div className="text-xs md:text-sm text-gray-400">{stat.label}</div>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="glass-effect rounded-xl p-5">
                  <h3 className="text-lg font-semibold text-white mb-4">Languages</h3>
                  <div className="space-y-3">
                    {[
                      { language: "English", level: "Fluent", percentage: 95 },
                      { language: "Urdu", level: "Native", percentage: 100 },
                      { language: "German", level: "Beginner", percentage: 30 }
                    ].map((item, index) => (
                      <div key={index} className="space-y-1">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-300">{item.language}</span>
                          <span className="text-primary-500">{item.level}</span>
                        </div>
                        <div className="w-full bg-gray-700 rounded-full h-1.5">
                          <motion.div
                            className="bg-gradient-to-r from-primary-500 to-secondary-500 h-1.5 rounded-full"
                            initial={{ width: 0 }}
                            animate={isInView ? { width: `${item.percentage}%` } : { width: 0 }}
                            transition={{ duration: 1, delay: 0.4 + index * 0.15, ease: 'easeOut' }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="glass-effect rounded-xl p-5">
                  <h3 className="text-lg font-semibold text-white mb-4">Interpersonal</h3>
                  <div className="flex flex-wrap gap-2">
                    {["Team Working", "Public Speaking", "Leadership"].map((skill, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, y: 20 }}
                        animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                        transition={{ duration: 0.4, delay: 0.3 + index * 0.1 }}
                      >
                        <StarBorder color="#764ba2" speed="6s">
                          <span className="text-sm text-gray-300">{skill}</span>
                        </StarBorder>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Skills — infinite marquee rows */}
          <motion.div variants={itemVariants}>
            <h3 className="text-2xl font-semibold text-white mb-6 text-center">Technical Skills</h3>
            <SkillsMarquee rows={skillRows} speed={36} />
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default About;
